/**
 * Status colours are load-bearing. They match the sheets staff read today:
 *
 *   RED   = requested / needs ordering
 *   GREEN = ordered
 *   GRAY  = received
 *
 * Do not invent a palette. Staff have read these colours for years, and the
 * whole pitch is that this replaces the sheet without retraining anyone.
 *
 * Red covers both `requested` and `approved` because both mean "this still
 * needs ordering", which is exactly what red means on their sheet.
 *
 * BACK-ORDER IS GLORIA'S OPEN QUESTION, NOT OURS. Her spec says "back ordered
 * = red *or* separate back-order status" — she left it undecided. We ship red
 * with a label, because it matches their current mental model and needs no new
 * vocabulary. `backOrderUsesOwnColour` in orgSettings flips it to amber. Ask
 * her at the demo; it is a thirty-second question that shows we read her
 * document properly.
 */

export type LineStatus =
  | "requested"
  | "approved"
  | "ordered-vendor"
  | "fulfilled-warehouse"
  | "back-ordered"
  | "cancelled"
  | "received";

export type StatusTone = "red" | "green" | "gray" | "amber";

export interface StatusPresentation {
  tone: StatusTone;
  label: string;
  /** Shown next to the pill when the tone alone is ambiguous — which is
   *  exactly the back-order case. */
  qualifier: string | null;
}

export function presentLineStatus(
  status: LineStatus,
  backOrderUsesOwnColour = false,
): StatusPresentation {
  switch (status) {
    case "requested":
      return { tone: "red", label: "Requested", qualifier: null };
    case "approved":
      return { tone: "red", label: "Approved", qualifier: "needs ordering" };
    case "ordered-vendor":
      return { tone: "green", label: "Ordered", qualifier: "from vendor" };
    case "fulfilled-warehouse":
      return { tone: "green", label: "Ordered", qualifier: "from warehouse" };
    case "back-ordered":
      return {
        tone: backOrderUsesOwnColour ? "amber" : "red",
        label: "Back order",
        qualifier: null,
      };
    case "cancelled":
      return { tone: "gray", label: "Cancelled", qualifier: null };
    case "received":
      return { tone: "gray", label: "Received", qualifier: null };
  }
}

/* -------------------------------------------------------------------------- */
/* v2 — proactive stocking                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Who raised a request: a person, or the weekly count.
 *
 * On `requests` the column is optional, and **absent means `manual`** — the
 * server resolves that default so no screen has to.
 */
export type RequestOrigin = "manual" | "top-up";

/**
 * The origin badge.
 *
 * Returns null for `manual`, and null means **draw nothing**: a request raised
 * by a person is the ordinary case and does not need a chip saying so. A badge
 * on every row is a badge nobody reads.
 *
 * Amber, because origin is a *qualifier* and never a status (SPEC, "Status
 * colours are load-bearing"): the row's red/green/gray pill still says where
 * the line is, and this sits beside it saying where it came from. No new
 * colour is invented — `TONE_CLASSES.amber` is the same amber urgency and a
 * modelled figure already use.
 */
export function presentRequestOrigin(
  origin: RequestOrigin,
): StatusPresentation | null {
  switch (origin) {
    case "manual":
      return null;
    case "top-up":
      return { tone: "amber", label: "Top-up", qualifier: "from the count" };
  }
}

/**
 * Whether a clinic's floor/target came from a human or from us.
 *
 * The `costBasis: "modelled"` pattern reused: seeded levels are our guess,
 * Deanna is bringing the real numbers, and a guess presented as a decision is
 * the thing that gets pointed at on a projector.
 */
export type LevelsBasis = "draft" | "confirmed";

/** Null for `confirmed` — a settled level wears no tag, the same way a manual
 *  request wears no origin badge. */
export function presentLevelsBasis(
  basis: LevelsBasis,
): StatusPresentation | null {
  switch (basis) {
    case "confirmed":
      return null;
    case "draft":
      return { tone: "amber", label: "Draft", qualifier: "not yet confirmed" };
  }
}

export type ReceiptStatus =
  | "pending"
  | "received"
  | "partial"
  | "missing"
  | "damaged"
  | "back-ordered";

/**
 * The outcomes a clinic can report on a delivery — `ReceiptStatus` minus
 * `pending`, which only the system sets. The Receive screen's buttons and the
 * Convex validator behind them both derive from this list, so a new outcome
 * ("wrong item", say) is recordable and validated with one edit.
 */
export const CLINIC_REPORTABLE_RECEIPTS = [
  "received",
  "partial",
  "missing",
  "damaged",
  "back-ordered",
] as const;

/**
 * "Delivered short" — everything except `pending` and a clean `received`.
 * The not-received and back-ordered reports both query per member of this
 * list; before it existed each carried its own copy, which is exactly how a
 * new outcome stays recordable on the Receive screen while being invisible on
 * both reports.
 */
export const SHORT_RECEIPT_STATUSES = [
  "partial",
  "missing",
  "damaged",
  "back-ordered",
] as const;

export function presentReceiptStatus(status: ReceiptStatus): StatusPresentation {
  switch (status) {
    case "pending":
      return { tone: "green", label: "In transit", qualifier: null };
    case "received":
      return { tone: "gray", label: "Received", qualifier: null };
    case "partial":
      return { tone: "red", label: "Partial", qualifier: "some missing" };
    case "missing":
      return { tone: "red", label: "Missing", qualifier: null };
    case "damaged":
      return { tone: "red", label: "Damaged", qualifier: null };
    case "back-ordered":
      return { tone: "red", label: "Back order", qualifier: null };
  }
}

export type InvoiceStatus =
  | "draft"
  | "sent"
  | "paid"
  | "partially-paid"
  | "overdue";

export function presentInvoiceStatus(status: InvoiceStatus): StatusPresentation {
  switch (status) {
    case "draft":
      return { tone: "gray", label: "Draft", qualifier: null };
    case "sent":
      return { tone: "green", label: "Sent", qualifier: null };
    case "paid":
      return { tone: "gray", label: "Paid", qualifier: null };
    case "partially-paid":
      return { tone: "amber", label: "Part paid", qualifier: null };
    case "overdue":
      return { tone: "red", label: "Overdue", qualifier: null };
  }
}

/**
 * Tailwind classes per tone. One place, so a colour change is one edit.
 *
 * Shades only — the hues above are the load-bearing part and are not ours to
 * change. These sit one step lighter than they used to (`-50` fills, `-200`
 * rings) because a screen carrying thirty of these pills was reading as thirty
 * warnings. The text stays at `-800`/`-900` so contrast goes up, not down,
 * while the fill goes quiet.
 */
export const TONE_CLASSES: Record<StatusTone, string> = {
  red: "bg-red-50 text-red-800 ring-red-200",
  green: "bg-green-50 text-green-800 ring-green-200",
  gray: "bg-gray-100 text-gray-600 ring-gray-200",
  amber: "bg-amber-50 text-amber-900 ring-amber-300",
};

/**
 * The 4px left rule that carries the same tone on a card.
 *
 * The pill fill above is deliberately quiet; the edge is the loud half, and it
 * is what the dashboard tiles, the inventory rows and the alert cards all use
 * to say "this one needs a person" without turning the card into a warning.
 * Three copies of this had already drifted — a tile's green was `-600` and an
 * inventory row's was `-500` — which is the whole reason it lives here now.
 *
 * Shades sit one step darker than the pill ring because a 4px rule has far less
 * area to carry the hue with. `gray` is *transparent*, not a grey line: grey
 * means settled — received, cancelled, acknowledged — and a card with nothing
 * left to do should not be drawing an edge. Every tone still carries the
 * `border-l-4`, so a card changing tone with live data cannot shift the layout
 * by four pixels mid-demo.
 */
export const TONE_EDGE: Record<StatusTone, string> = {
  red: "border-l-red-500",
  green: "border-l-green-600",
  gray: "border-l-transparent",
  amber: "border-l-amber-400",
};
